<template>
  <modal
    v-model="dialog"
    :title="`${isEditingExisting ? 'Edit' : 'Add'} ${label}`"
  >
    <template v-slot:activator="{ on, attrs }">
      <qvoButton
        v-bind="attrs"
        v-on="on"
        class="add-item-button"
        outlined
        leftIcon="mdi-plus-circle-outline"
      >
        Add Item
      </qvoButton>
    </template>
    <template #buttons>
      <qvoButton text @click="closeDialog">Cancel</qvoButton>
      <qvoButton @click="submit" :disabled="!isFormValid">Submit</qvoButton>
    </template>
    <v-form ref="form" v-model="isFormValid">
      <div v-for="field in allFields" :key="field.FieldId">
        <configField
          :id="ruleId"
          :category="category"
          :field="field"
          :rules="validationRules"
          @setConfigValue="setConfigValue"
          :value="values[field.FieldId]"
          emitSetConfig
          :visible="visibility[field.FieldId]"
          isInModal
        />
      </div>
    </v-form>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";

import qvoButton from "@/qux-common/qux22/components/molecules/button";
import modal from "@/qux-common/qux22/components/molecules/modal";
import fieldMap from "./field-map.json";
import getAllAvailableFields from "@/util/getAllAvailableFields";
import getValidationRules from "@/util/getValidationRules";
import visibleFields from "@/util/visibleFields";

export default {
  components: {
    qvoButton,
    modal,
    configField: () => import("../config-field"),
  },
  data() {
    return {
      dialog: false,
      values: {},
      isFormValid: false,
      isEditingExisting: false,
    };
  },
  props: {
    availableFieldList: {
      type: Array,
    },
    category: {
      type: String,
    },
    editValues: {
      type: Object,
    },
    id: {
      type: String,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    rules: {
      type: Array,
    },
    ruleId: {
      type: String,
    },
    value: {
      type: [String, Object, Number, Boolean, Array],
    },
  },
  methods: {
    getFieldValue(value, id = this.id) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][id] &&
        fieldMap[this.category][id][value]
      );
    },
    setConfigValue({ fieldId, value }) {
      this.values = Object.assign({}, this.values, { [fieldId]: value });
    },
    closeDialog() {
      this.dialog = false;
      this.$emit("clearEditValues");
      this.isEditingExisting = false;
    },
    submit() {
      this.$emit("addItem", this.values, this.isEditingExisting);
      this.closeDialog();
    },
  },
  computed: {
    validationRules() {
      return getValidationRules({
        rules: this.rules,
        inputErrorMessage: this.getFieldValue("inputErrorMessage"),
      });
    },
    allFields() {
      return getAllAvailableFields(this.availableFieldList);
    },
    visibility() {
      const mergedConfig = cloneDeep(this.mergedConfig);

      const values = mergedConfig[this.ruleId][this.category];
      merge(values, this.values);

      mergedConfig[this.ruleId][this.category] = values;

      // This contains any currently open templates or clients
      const configList = Object.keys(this.config).map(configId => { return { ClientInfoRuleId: configId } });

      const fields = visibleFields({
        config: mergedConfig,
        configList,
        configTemplate: this.configTemplate,
        roles: this.roles,
      });

      const fieldData = {};

      const checkFields = (availableFieldList) => {
        for (const field of availableFieldList) {
          fieldData[field.FieldId] = !!(
            fields[this.ruleId] &&
            fields[this.ruleId][field.category || this.category] &&
            fields[this.ruleId][field.category || this.category][field.FieldId]
          );
          if (field.AvailableFieldList) {
            checkFields(field.AvailableFieldList);
          }
          if (field.Options) {
            for (const option of field.Options) {
              if (option.AvailableFieldList) {
                checkFields(option.AvailableFieldList);
              }
            }
          }
        }
      };

      checkFields(this.availableFieldList);
      return fieldData;
    },
    ...mapGetters({
      config: "getConfig",
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
      roles: "getRoles",
    }),
  },
  watch: {
    dialog() {
      if (!this.dialog) {
        this.values = {};
        this.$refs.form.reset();
      }
    },
    editValues() {
      if (this.editValues && Object.keys(this.editValues).length > 0) {
        this.values = this.editValues;
        this.dialog = true;
        this.isEditingExisting = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fields {
  display: grid;
  gap: $qvo-spacing-16;
}
.field-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr;
  gap: $qvo-spacing-16;
}
</style>